import { useState, type FormEvent } from 'react'
import { Link, Navigate, useLocation } from 'react-router'
import { Button, Input } from '@waago/ui'
import { useAuth, type LoginChallenge } from '../../lib/auth'
import { ApiError } from '../../api/client'
import { DemoLogins, demoLoginsEnabled, fetchDemoCode, type DemoAccount } from './DemoLogins'

/** BO-001 admin login. Design: design/png/BO/001_Login_Desktop_-_1.png */
export function LoginPage() {
  const { state, login, verifyLogin, resendLoginCode } = useAuth()
  const location = useLocation() as { state?: { from?: string } }
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  /** Set once the password is accepted and a code has been sent. */
  const [challenge, setChallenge] = useState<LoginChallenge>(null)
  const [code, setCode] = useState('')
  /**
   * When a code was last asked for, in epoch milliseconds.
   *
   * <p>The development log keeps only the last code per address, so on a resend
   * — or a second sign-in with the same account — the previous, already-consumed
   * code is what comes back. Fetching "a code delivered after this" waits for
   * the real one instead of filling in a dead one.
   */
  const [askedAt, setAskedAt] = useState(0)

  if (state.status === 'authenticated') {
    return <Navigate to={location.state?.from ?? '/'} replace />
  }

  async function attempt(withEmail: string, withPassword: string) {
    setError(null)
    setBusy(true)
    // Marked before the request, not after: the code is recorded while the
    // response is still in flight, and a mark taken afterwards can land past it.
    setAskedAt(Date.now())
    try {
      const next = await login(withEmail, withPassword)
      if (next) {
        setChallenge(next)
        setCode('')
      }
    } catch (err) {
      // Never disclose whether the address exists — that is an account
      // enumeration oracle.
      setError(
        err instanceof ApiError && err.status === 429
          ? 'Trop de tentatives. Réessayez dans quelques minutes.'
          : 'Identifiants incorrects.',
      )
    } finally {
      setBusy(false)
    }
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    await attempt(email, password)
  }

  async function onVerify(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setBusy(true)
    try {
      await verifyLogin(email, password, code)
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 429
          ? 'Trop de tentatives. Réessayez dans quelques minutes.'
          : 'Code incorrect ou expiré.',
      )
    } finally {
      setBusy(false)
    }
  }

  async function onResend() {
    setError(null)
    setBusy(true)
    setAskedAt(Date.now())
    try {
      await resendLoginCode(email, password)
      setCode('')
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 429
          ? 'Trop de codes demandés. Réessayez dans quelques minutes.'
          : 'Le code n’a pas pu être renvoyé.',
      )
    } finally {
      setBusy(false)
    }
  }

  /**
   * One click means one click. A staff password buys a challenge rather than a
   * session, so this finishes it too — otherwise the demo button lands on a
   * screen asking for a code that only exists in a hashed column.
   */
  async function onDemoPick(account: DemoAccount) {
    // Fill the form as well, so it is obvious which account was used.
    setEmail(account.email)
    setPassword(account.password)
    setError(null)
    setBusy(true)
    try {
      // Taken before the request, not after: the code is recorded while the
      // response is still in flight, and a timestamp read afterwards can land
      // past it and reject the very code this click asked for.
      const asked = Date.now()
      const next = await login(account.email, account.password)
      if (!next) return
      setChallenge(next)

      const delivered = await fetchDemoCode(account.email, asked)
      if (delivered === null) {
        setCode('')
        setError('Aucun code livré. Saisissez-le manuellement ou réessayez.')
        return
      }
      setCode(delivered)
      await verifyLogin(account.email, account.password, delivered)
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 429
          ? 'Trop de tentatives. Réessayez dans quelques minutes.'
          : 'Connexion de démonstration indisponible.',
      )
    } finally {
      setBusy(false)
    }
  }

  /** Fills the code from where it was delivered, for the manual demo path. */
  async function onFetchDemoCode() {
    setError(null)
    setBusy(true)
    try {
      const delivered = await fetchDemoCode(email, askedAt)
      if (delivered === null) {
        setError('Aucun code livré à cette adresse.')
        return
      }
      setCode(delivered)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="grid min-h-dvh lg:grid-cols-2">
      <div className="bg-brand-800 hidden flex-col justify-between p-10 text-white lg:flex">
        <div>
          <p className="text-2xl font-bold tracking-tight">WAAGO</p>
          <p className="mt-1 text-xs text-white/90">Move. Deliver. Connect.</p>
        </div>
        <p className="max-w-sm text-lg leading-relaxed text-white/95">
          Plateforme d’intermédiation pour le transport de personnes, de marchandises et la location
          d’engins au Bénin.
        </p>
        <p className="text-2xs text-white/80">© {new Date().getFullYear()} WaaGo</p>
      </div>

      {challenge ? (
        // "Code de vérification" — BO frames `4081:467` (SMS) and `4081:537`
        // (e-mail). The password is kept in state and sent again with the code:
        // a challenge a code alone could finish would let anyone holding the
        // phone in without ever knowing the password.
        <div className="flex items-center justify-center p-6">
          <form onSubmit={(e) => void onVerify(e)} className="w-full max-w-sm" noValidate>
            <h1 className="text-2xl font-bold text-slate-900">Code de vérification</h1>
            <p className="mt-1 mb-6 text-sm text-slate-500">
              Veuillez entrer le code de confirmation de votre compte envoyé{' '}
              {challenge.channel === 'EMAIL' ? 'au ' : 'au '}
              <span className="font-semibold text-slate-900">{challenge.destination}</span>
            </p>

            {error ? (
              <div
                role="alert"
                className="animate-slide-up mb-4 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700"
              >
                {error}
              </div>
            ) : null}

            <Input
              label="Code"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="000000"
              required
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            />

            <Button
              type="submit"
              block
              size="lg"
              className="mt-6"
              disabled={busy || code.length < 6}
            >
              {busy ? 'Vérification…' : 'Vérifier'}
            </Button>

            <p className="mt-4 text-center text-xs text-slate-600">
              Aucun code reçu ?{' '}
              <button
                type="button"
                className="text-danger underline"
                disabled={busy}
                onClick={() => void onResend()}
              >
                Renvoyer
              </button>
            </p>

            {/* Development only, same guard as the one-click buttons: there is
                no way to read a delivered code otherwise, and a screen that
                cannot be got past is a screen nobody can test. */}
            {demoLoginsEnabled() ? (
              <Button
                type="button"
                variant="outline"
                block
                className="mt-3"
                disabled={busy}
                data-testid="demo-fill-code"
                onClick={() => void onFetchDemoCode()}
              >
                Récupérer le code (démo)
              </Button>
            ) : null}

            <button
              type="button"
              className="text-danger mt-6 block w-full text-center text-xs underline"
              onClick={() => {
                setChallenge(null)
                setError(null)
              }}
            >
              Se connecter
            </button>
          </form>
        </div>
      ) : (
        <div className="flex items-center justify-center p-6">
          <form onSubmit={(e) => void onSubmit(e)} className="w-full max-w-sm" noValidate>
            <h1 className="text-2xl font-bold text-slate-900">Connexion</h1>
            <p className="mt-1 mb-6 text-xs text-slate-500">Accès réservé aux administrateurs.</p>

            {error ? (
              <div
                role="alert"
                className="animate-slide-up mb-4 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700"
              >
                {error}
              </div>
            ) : null}

            <div className="flex flex-col gap-4">
              <Input
                label="Email"
                type="email"
                name="email"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <Input
                label="Mot de passe"
                type="password"
                name="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>

            <Button type="submit" block size="lg" className="mt-6" disabled={busy}>
              {busy ? 'Connexion…' : 'Se connecter'}
            </Button>

            <Link
              to="/mot-de-passe-oublie"
              className="text-brand-600 mt-4 block text-center text-xs hover:underline"
            >
              Mot de passe oublié ?
            </Link>

            <DemoLogins onPick={(a) => void onDemoPick(a)} busy={busy} />
          </form>
        </div>
      )}
    </div>
  )
}
